// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { WFParametersDto } from '../../shared-workflow-module/_models/wf-parameter-dto';
import { WFESpecialHandlingResultDto } from '../../shared-workflow-module/_models/wfe-special-handling-result-dto';
import { WorkflowHistoryResultDto } from '../../shared-workflow-module/_models/wfe-workflow-history-result-dto';
import { WorkFlowParametersDto } from '../../shared-workflow-module/_models/workflow-parameters-dto';
import { BaseWorkFlowWorkOrderParametersDto } from '../_models/common/base-wf-wo-parameter-dto';
import { WorkFlowWorkOrderResultDto } from '../_models/common/wf-work-order-result-dto';
import { WFSpecialHandlingParametersDto } from '../_models/workflow-search/wf-special-handling-parameters-dto';
import { WFReverseAPBillParametersDto } from '../_models/workflow-search/wf-reverse-ap-bill-parameters-dto';
import { WFEVendorResultDto } from '../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEOwnerResultDto } from '../../shared-workflow-module/_models/wfe-owner-result-dto';
import { WorkFlowManageWorkOrderParametersDto } from '../_models/common/manage-work-order-parameters-dto';

// constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from './../../_constants/cache-duration';

@Injectable({
    providedIn: 'root'
})
export class WorkflowService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getSpecialHandling(baseParams: WorkFlowParametersDto): Observable<WFESpecialHandlingResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}specialhandling${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFESpecialHandlingResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFESpecialHandlingResultDto, response)));
    }

    updateSpecialHandling(baseParams: WFSpecialHandlingParametersDto): Observable<string> {
        return this.httpClient
            .put<string>(`${URLConstants.ccWorkflowViewerBaseApiUrl}specialhandling`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    toggleWorkFlowPaymentMethod(baseParams: WorkFlowParametersDto): Observable<string> {
        return this.httpClient
            .put<string>(`${URLConstants.ccWorkflowViewerBaseApiUrl}togglepaymentmethod`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    updateToReturnToAccountant(baseParams: WorkFlowParametersDto): Observable<string> {
        return this.httpClient
            .put<string>(`${URLConstants.ccWorkflowViewerBaseApiUrl}returnToAccountant`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    updateReverseAPBill(baseParams: WFReverseAPBillParametersDto): Observable<string> {
        return this.httpClient
            .put<string>(`${URLConstants.ccWorkflowViewerBaseApiUrl}reverse`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    getViewDeleteReason(baseParam: WorkFlowParametersDto): Observable<string> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}deletereason${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient.get(url, options).pipe(map((response) => response.toString()));
    }

    getBoardHistory(baseParam: WorkFlowParametersDto): Observable<WorkflowHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}board/history${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WorkflowHistoryResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WorkflowHistoryResultDto, response)));
    }

    getVendors(baseParam: WFParametersDto): Observable<WFEVendorResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendors${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEVendorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEVendorResultDto, response)));
    }

    getOwners(baseParam: WFParametersDto): Observable<WFEOwnerResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}owners${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFEOwnerResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(WFEOwnerResultDto, response)));
    }

    getOpenWorkOrders(baseParams: BaseWorkFlowWorkOrderParametersDto): Observable<WorkFlowWorkOrderResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/open${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkFlowWorkOrderResultDto, response)));
    }

    manageWorkOrders(baseParams: WorkFlowManageWorkOrderParametersDto): Observable<any> {
        return this.httpClient
            .post<any>(`${URLConstants.ccWorkflowViewerBaseApiUrl}workorders/manage`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData));
    }
}
